/**
 * The support queue.
 *
 * Rows are pills, not table cells. This was one of the seven screens still
 * rendering the pre-redesign `<table>`, and it had the other problem those
 * tables shared: the row navigated from `<tr onClick>`, so with a keyboard the
 * rows did not exist at all. The subject is now a link that stretches an
 * `::after` over the whole row, which keeps the row clickable end to end and
 * puts it in the tab order where it belongs.
 *
 * Status and priority were ten hand-written Tailwind colours across two maps;
 * they are status pills now - see tone.ts for which tone means what and why
 * most rows deliberately stay quiet.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlarmClock, BookText, LifeBuoy, Loader2, MessageSquareText, Plus, Sprout, UserCheck, Voicemail,
} from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  deleteTicket, getTicketTags, getTickets, ticketParty, updateTicket,
  type Ticket, type TicketPriority, type TicketStatus,
} from "../../../lib/support";
import {
  Avatar, Btn, EmptyState, MenuField, PageHead, SearchField, StatusPill, Toolbar, ToolbarGap, fmtStamp, listCard,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { useStaff } from "../_shared/staff";
import { NewTicketModal } from "./NewTicketModal";
import { CannedRepliesModal } from "./CannedRepliesModal";
import { TicketRowMenu } from "./TicketRowMenu";
import { SlaFlag } from "./SlaBadge";
import { PRIORITIES, STATUSES, priorityTone, staffName, statusTone } from "./tone";

/**
 * Number+subject, client, status, priority, assignee, updated.
 *
 * The last header used to read "Viimati muudetud", which wants 178px on one
 * line - more than the column's own content ("6. aug") will ever need, and it
 * wrapped instead, making this head row 16px taller than every other list in
 * the CRM. The label is one word now; the track is sized for the dates.
 */
const COLS = "minmax(0,2.4fr) 1.05fr .95fr .95fr 1fr .9fr 44px";

const ALL = "all";

export function TicketsList() {
  const t = useT();
  const [rows, setRows] = useState<Ticket[] | null>(null);
  // The shared directory rather than a fetch of its own: opening a ticket from
  // this list used to ask for the same twenty rows a second time, and the local
  // copy arrived in whatever order the route sent - which is not the order an
  // assignee picker may offer people in (see `_shared/staff.ts`).
  const { staff } = useStaff();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<TicketStatus | typeof ALL>(ALL);
  const [priority, setPriority] = useState<TicketPriority | typeof ALL>(ALL);
  const [mine, setMine] = useState(false);
  const [lateOnly, setLateOnly] = useState(false);
  /**
   * Whether the desk has promised anything at all.
   *
   * Starts false so the filter is absent on the very first paint rather than
   * appearing and then vanishing. A control that can only ever return an empty
   * page is worse than no control - the operator reads the empty result as
   * "nothing is late" when the truth is "nothing was ever promised".
   */
  const [slaOn, setSlaOn] = useState(false);
  const [tag, setTag] = useState<string>(ALL);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [confirmDel, setConfirmDel] = useState<Ticket | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getTickets({
        pageSize: 100,
        q: q || undefined,
        status: status === ALL ? undefined : status,
        priority: priority === ALL ? undefined : priority,
        mine: mine || undefined,
        sla: lateOnly ? "breached" : undefined,
        tag: tag === ALL ? undefined : tag,
      });
      setRows(res.rows);
      setSlaOn(res.slaConfigured);
    } catch {
      setRows([]);
    }
  }, [q, status, priority, mine, lateOnly, tag]);

  useEffect(() => {
    const id = setTimeout(() => void load(), 200);
    return () => clearTimeout(id);
  }, [load]);

  // Once, not per filter change: the vocabulary is a menu, and reloading it on
  // every selection would make the options flicker while they are being read.
  useEffect(() => {
    let alive = true;
    getTicketTags()
      .then((res) => { if (alive) setTagOptions(res.rows); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  async function setStatusOn(ticket: Ticket, next: TicketStatus) {
    // Written through and reflected locally rather than reloaded: the queue is
    // often filtered by status, and a reload would make the row you just moved
    // vanish from under the cursor before you saw it change.
    const updated = await updateTicket(ticket.id, { status: next });
    setRows((prev) => (prev ?? []).map((r) => (r.id === ticket.id ? { ...r, ...updated } : r)));
  }

  async function remove(ticket: Ticket) {
    try {
      await deleteTicket(ticket.id);
      setRows((prev) => (prev ?? []).filter((r) => r.id !== ticket.id));
    } finally {
      setConfirmDel(null);
    }
  }

  const filtered = q !== "" || status !== ALL || priority !== ALL || mine || lateOnly || tag !== ALL;

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead
        title={t("support.title")}
        sub={t("support.intro")}
        actions={
          <>
            <Btn onClick={() => setShowCanned(true)}>
              <BookText className="size-4" /> {t("support.cannedReplies")}
            </Btn>
            <Btn tone="brand" onClick={() => setShowNew(true)}>
              <Plus className="size-4" /> {t("support.add")}
            </Btn>
          </>
        }
      />

      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("support.search")} />
        <MenuField
          value={status}
          onChange={setStatus}
          label={t("support.statusCol")}
          options={[
            { value: ALL, label: t("support.allStatuses") },
            ...STATUSES.map((s) => ({ value: s as TicketStatus | typeof ALL, label: t(`support.statusLabel.${s}`) })),
          ]}
        />
        <MenuField
          value={priority}
          onChange={setPriority}
          label={t("support.priority")}
          options={[
            { value: ALL, label: t("support.allPriorities") },
            ...PRIORITIES.map((p) => ({
              value: p as TicketPriority | typeof ALL,
              label: t(`support.priorityLabel.${p}`),
            })),
          ]}
        />
        {/* Absent until there is a label to pick. An empty menu reads as a
            broken control rather than as an empty vocabulary. */}
        {tagOptions.length > 0 ? (
          <MenuField
            value={tag}
            onChange={setTag}
            label={t("support.tags")}
            options={[
              { value: ALL, label: t("support.allTags") },
              ...tagOptions.map((tg) => ({ value: tg, label: tg })),
            ]}
          />
        ) : null}
        <ToolbarGap />
        {/* Only once there is a promise to break. See `slaOn`. */}
        {slaOn ? (
          <button
            type="button"
            aria-pressed={lateOnly}
            onClick={() => setLateOnly((v) => !v)}
            className={`crm-cb crm-cb-wide${lateOnly ? " is-on" : ""}`}
          >
            <AlarmClock className="size-4" />
            {t("support.sla.filter")}
          </button>
        ) : null}
        <button
          type="button"
          aria-pressed={mine}
          onClick={() => setMine((m) => !m)}
          className={`crm-cb crm-cb-wide${mine ? " is-on" : ""}`}
        >
          <UserCheck className="size-4" />
          {t("support.myTickets")}
        </button>
      </Toolbar>

      <div className="crm-card" style={listCard(COLS)}>
        {rows === null ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          // "No tickets at all" and "nothing matches these four filters" are
          // different facts, and only one of them means the queue is clear.
          <EmptyState
            icon={LifeBuoy}
            title={t(filtered ? "support.noMatch" : "support.empty")}
            body={t(filtered ? "support.noMatchBody" : "support.emptyBody")}
            action={
              filtered ? null : (
                <Btn tone="brand" onClick={() => setShowNew(true)}>
                  <Plus className="size-4" /> {t("support.add")}
                </Btn>
              )
            }
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("support.subject")}</span>
              <span>{t("support.client")}</span>
              <span>{t("support.statusCol")}</span>
              <span>{t("support.priority")}</span>
              <span>{t("support.assignee")}</span>
              <span>{t("support.updated")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0">
              {rows.map((tk) => {
                // The party is a client OR a juhuklient - a voicemail ticket is
                // almost always the second, and a column that only read `client`
                // printed a dash on every one of them.
                const party = ticketParty(tk);
                return (
                <div key={tk.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar name={party?.name ?? tk.contactName ?? tk.subject} />
                    <span className="min-w-0">
                      <Link
                        to={`/support/${tk.id}`}
                        className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
                      >
                        {tk.subject}
                      </Link>
                      <span className="crm-prow-s crm-trunc flex items-center gap-2">
                        <span className="crm-tnum">{tk.number}</span>
                        {tk._count ? (
                          <span className="inline-flex items-center gap-1">
                            <MessageSquareText className="size-3" />
                            <span className="crm-tnum">{tk._count.messages}</span>
                          </span>
                        ) : null}
                        {/* Where this came from, when it came from a phone. The
                            subject is the AI's sentence, and knowing it was
                            spoken rather than written changes how it reads. */}
                        {tk.callLog ? (
                          <span
                            className="inline-flex items-center gap-1"
                            title={t("support.fromCall")}
                          >
                            <Voicemail className="size-3" />
                          </span>
                        ) : null}
                        {/* Silent unless the promise is nearly out or already
                            broken - see SlaBadge.tsx. */}
                        <SlaFlag sla={tk.sla} />
                        {/* Read-only here; the labels are set on the ticket's own
                            page, where there is room for a chip control. */}
                        {tk.tags.map((tg) => (
                          <span key={tg} className="crm-chip">
                            {tg}
                          </span>
                        ))}
                      </span>
                    </span>
                  </span>

                  <span className="crm-mut crm-trunc flex items-center gap-1.5 text-[12.5px]">
                    {party?.kind === "lead" ? <Sprout className="size-3 flex-none" /> : null}
                    <span className="crm-trunc">{party?.name ?? tk.contactName ?? "-"}</span>
                  </span>
                  <span>
                    <StatusPill tone={statusTone(tk.status)}>{t(`support.statusLabel.${tk.status}`)}</StatusPill>
                  </span>
                  <span>
                    <StatusPill tone={priorityTone(tk.priority)}>
                      {t(`support.priorityLabel.${tk.priority}`)}
                    </StatusPill>
                  </span>
                  <span className="crm-mut crm-trunc text-[12.5px]">{staffName(staff, tk.assignedTo)}</span>
                  <span className="crm-mut crm-tnum text-[12.5px]">{fmtStamp(tk.updatedAt)}</span>

                  <TicketRowMenu
                    ticket={tk}
                    onStatus={(s) => void setStatusOn(tk, s)}
                    onDelete={() => setConfirmDel(tk)}
                  />
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {showNew ? (
        <NewTicketModal staff={staff} onClose={() => setShowNew(false)} onCreated={() => void load()} />
      ) : null}
      {showCanned ? <CannedRepliesModal onClose={() => setShowCanned(false)} /> : null}
      {confirmDel ? (
        <ConfirmModal
          title={t("support.deleteTitle")}
          body={t("support.deleteConfirm")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(confirmDel)}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
